/**
 * useChartNavigation Hook
 *
 * Provides navigation functionality for chart components in TerraMatrix.
 * Handles click events on chart elements (bars, lines, pie slices, etc.)
 * and triggers page navigation based on configured mappings.
 *
 * Created: January 2026 (Phase 1: Chart Navigation)
 * @see Documents/Roadmaps/Chart_Navigation_Roadmap.md
 */

import { useCallback, useMemo } from 'react';
import { Component } from '@/types';
import {
  ChartNavigationConfig,
  ChartNavigationMapping,
  UseChartNavigationReturn,
} from '@/types/chartNavigation';
import { NavigationConfig, InteractionHandlers } from '@/components/shared/rendering/InteractionContext';

/**
 * Interaction object shape from useComponentInteraction hook.
 * We define this locally to avoid circular dependencies.
 */
interface ComponentInteraction {
  canInteract: boolean;
  handlers: Partial<InteractionHandlers>;
  mode: 'editing' | 'viewing' | 'functional' | 'pin-placement';
  capabilities: {
    canSelect: boolean;
    canResize: boolean;
    canDrag: boolean;
    canInteract: boolean;
    canNavigate: boolean;
    canEditProperties: boolean;
    canDelete: boolean;
  };
}

/**
 * Hook for handling chart navigation in TerraMatrix components.
 *
 * Reads navigation configuration from component.properties.navigation and provides
 * handlers for click events. Integrates with InteractionContext for page navigation.
 *
 * @param component - The chart component instance
 * @param interaction - Result from useComponentInteraction hook
 * @returns Navigation handlers and state
 *
 * @example
 * ```tsx
 * // In BarChart.tsx
 * const interaction = useComponentInteraction(component);
 * const { handleChartClick, isNavigable, getNavigationForElement } = useChartNavigation(component, interaction);
 *
 * // On Bar click
 * <Bar onClick={(data, index) => handleChartClick(data.payload, barKey, index)} />
 *
 * // For cursor styling
 * style={{ cursor: getNavigationForElement(barKey, entry.name)?.cursor || 'default' }}
 * ```
 */
export function useChartNavigation(
  component: Component,
  interaction: ComponentInteraction
): UseChartNavigationReturn {
  /**
   * Extract navigation config from component properties.
   * Cast to ChartNavigationConfig or undefined.
   */
  const navigationConfig = useMemo<ChartNavigationConfig | undefined>(() => {
    const navProp = component.properties?.navigation;
    if (navProp && typeof navProp === 'object' && 'enabled' in navProp) {
      return navProp as ChartNavigationConfig;
    }
    return undefined;
  }, [component.properties?.navigation]);

  /**
   * Determine if the chart has any valid navigation configured.
   */
  const isNavigable = useMemo<boolean>(() => {
    if (!navigationConfig?.enabled) return false;
    // Has mappings OR has a default target
    return (
      navigationConfig.mappings.length > 0 ||
      (navigationConfig.defaultAction === 'navigate' && !!navigationConfig.defaultTarget)
    );
  }, [navigationConfig]);

  /**
   * Find the matching navigation mapping for a given series/data point.
   *
   * Priority order:
   * 1. Exact seriesKey match (case-insensitive)
   * 2. Exact dataPointValue match (case-insensitive, converted to string)
   * 3. Default action with default target
   * 4. null (no navigation)
   */
  const findNavigationMapping = useCallback(
    (seriesKey?: string, dataPointValue?: string): ChartNavigationMapping | null => {
      if (!navigationConfig?.enabled) {
        return null;
      }

      const { mappings, defaultAction, defaultTarget } = navigationConfig;

      // 1. Try series match first (case-insensitive)
      if (seriesKey) {
        const seriesMatch = mappings.find(
          (m) => m.seriesKey?.toLowerCase() === seriesKey.toLowerCase()
        );
        if (seriesMatch) {
          console.log('[ChartNav] Found series match:', seriesKey, '->', seriesMatch.targetPageId);
          return seriesMatch;
        }
      }

      // 2. Try data point value match (case-insensitive, string comparison)
      if (dataPointValue !== undefined && dataPointValue !== null) {
        const valueStr = String(dataPointValue).toLowerCase();
        const pointMatch = mappings.find(
          (m) => m.dataPointValue?.toLowerCase() === valueStr
        );
        if (pointMatch) {
          console.log('[ChartNav] Found data point match:', dataPointValue, '->', pointMatch.targetPageId);
          return pointMatch;
        }
      }

      // 3. Check default action
      if (defaultAction === 'navigate' && defaultTarget) {
        console.log('[ChartNav] Using default target:', defaultTarget);
        return {
          targetPageId: defaultTarget,
          cursor: 'pointer',
        };
      }

      // 4. No match found
      console.log('[ChartNav] No mapping found for:', { seriesKey, dataPointValue });
      return null;
    },
    [navigationConfig]
  );

  /**
   * Build navigation parameters from clicked data and mapping configuration.
   *
   * Merges:
   * 1. Static parameters from mapping
   * 2. Data point values (if passDataPointAsParams or config.passDataAsParams)
   * 3. Applies parameterMapping transforms
   */
  const buildNavigationParameters = useCallback(
    (
      payload: Record<string, unknown>,
      mapping: ChartNavigationMapping
    ): Record<string, unknown> => {
      let parameters: Record<string, unknown> = {};

      // 1. Add static parameters from mapping
      if (mapping.parameters) {
        parameters = { ...mapping.parameters };
      }

      // 2. Add data point values if configured
      const shouldPassData =
        mapping.passDataPointAsParams || navigationConfig?.passDataAsParams;

      if (shouldPassData) {
        // Filter out internal/sensitive fields
        const filteredPayload: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(payload)) {
          // Skip internal fields (starting with _ or containing internal keywords)
          if (!key.startsWith('_') && !key.includes('__')) {
            filteredPayload[key] = value;
          }
        }
        parameters = { ...parameters, ...filteredPayload };
      }

      // 3. Apply parameter mapping transforms (mapping-level takes precedence over config-level)
      const parameterMapping = mapping.parameterMapping || navigationConfig?.parameterMapping;
      if (parameterMapping) {
        for (const [dataKey, paramKey] of Object.entries(parameterMapping)) {
          if (payload[dataKey] !== undefined) {
            parameters[paramKey] = payload[dataKey];
          }
        }
      }

      return parameters;
    },
    [navigationConfig?.passDataAsParams, navigationConfig?.parameterMapping]
  );

  /**
   * Check if navigation conditions are met.
   */
  const checkConditions = useCallback(
    (payload: Record<string, unknown>, conditions?: ChartNavigationMapping['conditions']): boolean => {
      if (!conditions || conditions.length === 0) {
        return true;
      }

      return conditions.every((condition) => {
        const fieldValue = payload[condition.field];

        switch (condition.operator) {
          case 'equals':
            return fieldValue === condition.value;
          case 'contains':
            return String(fieldValue).includes(String(condition.value));
          case 'greater':
            return Number(fieldValue) > Number(condition.value);
          case 'less':
            return Number(fieldValue) < Number(condition.value);
          default:
            return true;
        }
      });
    },
    []
  );

  /**
   * Handle click events from chart elements.
   * Finds the matching navigation mapping and triggers page navigation.
   */
  const handleChartClick = useCallback(
    async (
      payload: Record<string, unknown>,
      seriesKey?: string,
      dataIndex?: number
    ): Promise<void> => {
      console.log('[ChartNav] Click received:', {
        componentId: component.id,
        seriesKey,
        dataIndex,
        payload,
      });

      // Check if navigation handler is available
      if (!interaction.handlers.onNavigate) {
        console.log('[ChartNav] No onNavigate handler available (not in functional mode?)');
        return;
      }

      // Check if navigation is enabled
      if (!navigationConfig?.enabled) {
        console.log('[ChartNav] Navigation not enabled for component:', component.id);
        return;
      }

      // Get xAxisKey from component properties for data point value lookup
      const xAxisKey = (component.properties?.xAxisKey as string) || 'name';
      const dataPointValue = payload[xAxisKey] as string | undefined;

      // Find matching mapping
      const mapping = findNavigationMapping(seriesKey, dataPointValue);

      if (!mapping) {
        console.log('[ChartNav] No navigation mapping found');
        return;
      }

      // Check conditions
      if (!checkConditions(payload, mapping.conditions)) {
        console.log('[ChartNav] Conditions not met, skipping navigation');
        return;
      }

      // Build parameters
      const parameters = buildNavigationParameters(payload, mapping);

      console.log('[ChartNav] Navigating:', {
        targetPageId: mapping.targetPageId,
        parameters,
        transition: mapping.transition,
      });

      // Build NavigationConfig for the handler
      const navConfig: NavigationConfig = {
        targetPageId: mapping.targetPageId,
        parameters,
        transition: mapping.transition,
        conditions: mapping.conditions,
      };

      // Trigger navigation
      try {
        const result = await interaction.handlers.onNavigate(navConfig);
        console.log('[ChartNav] Navigation result:', result);
      } catch (error) {
        console.error('[ChartNav] Navigation error:', error);
      }
    },
    [
      component.id,
      component.properties?.xAxisKey,
      navigationConfig?.enabled,
      interaction.handlers,
      findNavigationMapping,
      buildNavigationParameters,
      checkConditions,
    ]
  );

  /**
   * Get navigation mapping for a specific element.
   * Use for determining cursor style and hover effects.
   */
  const getNavigationForElement = useCallback(
    (seriesKey?: string, dataPointValue?: string): ChartNavigationMapping | null => {
      return findNavigationMapping(seriesKey, dataPointValue);
    },
    [findNavigationMapping]
  );

  return {
    handleChartClick,
    isNavigable,
    getNavigationForElement,
    navigationConfig,
  };
}

export default useChartNavigation;
